import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import {
  LayoutDashboard, Calendar, Users, Car, ClipboardList, BrainCircuit,
  Wrench, KanbanSquare, PaintBucket, ShieldCheck, Boxes, Wallet, FileText,
  BarChart3, Settings, Sun, Moon, LogOut, Search, LogIn, ScanLine, Menu, X,
  MoreHorizontal, Archive,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import BrandLogo from "@/components/BrandLogo";
import InstallPWA from "@/components/InstallPWA";
import { BRAND } from "@/lib/brand";

// Rotas permitidas para o perfil Mechanic (todo o resto é restrito a Admin)
const MECHANIC_ALLOWED_PATHS = new Set([
  "/dashboard",  // dashboard operacional é útil pro técnico ver os KPIs do dia
  "/check-in",
  "/check-status",
  "/agenda",
  "/inspecoes",
  "/diagnostico",
  "/fila",
  "/kanban",
]);

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/check-in", label: "Check-In", icon: LogIn, testId: "nav-check-in", highlight: true },
  { to: "/check-status", label: "Check Status", icon: ScanLine, testId: "nav-check-status", highlight: true },
  { to: "/agenda", label: "Agenda", icon: Calendar, testId: "nav-agenda" },
  { to: "/clientes", label: "Clientes", icon: Users, testId: "nav-clientes", adminOnly: true },
  { to: "/veiculos", label: "Veículos", icon: Car, testId: "nav-veiculos", adminOnly: true },
  { to: "/inspecoes", label: "Inspeções", icon: ClipboardList, testId: "nav-inspecoes" },
  { to: "/diagnostico", label: "Diagnóstico IA", icon: BrainCircuit, testId: "nav-diagnostico" },
  { to: "/fila", label: "Fila dos Mecânicos", icon: Wrench, testId: "nav-fila" },
  { to: "/kanban", label: "Kanban Oficina", icon: KanbanSquare, testId: "nav-kanban" },
  { to: "/body-shop", label: "Kanban Body Shop", icon: PaintBucket, testId: "nav-body-shop", adminOnly: true },
  { to: "/garantias", label: "Garantias Estendidas", icon: ShieldCheck, testId: "nav-garantias", adminOnly: true },
  { to: "/inventario", label: "Inventário", icon: Boxes, testId: "nav-inventario", adminOnly: true },
  { to: "/financeiro", label: "Financeiro", icon: Wallet, testId: "nav-financeiro", adminOnly: true },
  { to: "/fechamento", label: "Fechamento", icon: FileText, testId: "nav-fechamento", adminOnly: true },
  { to: "/arquivados", label: "Arquivados", icon: Archive, testId: "nav-arquivados", adminOnly: true },
  { to: "/relatorios", label: "Relatórios", icon: BarChart3, testId: "nav-relatorios", adminOnly: true },
  { to: "/configuracoes", label: "Configurações", icon: Settings, testId: "nav-configuracoes", adminOnly: true },
];

// Bottom nav apenas 4 itens principais + Mais
const BOTTOM_NAV = [
  { to: "/dashboard", label: "Home", icon: LayoutDashboard, testId: "bnav-dashboard" },
  { to: "/check-in", label: "Check-In", icon: LogIn, testId: "bnav-checkin" },
  { to: "/kanban", label: "Kanban", icon: KanbanSquare, testId: "bnav-kanban" },
  { to: "/fechamento", label: "Fech.", icon: FileText, testId: "bnav-fechamento" },
];

export default function AppShell() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  // Fecha drawer/search ao trocar de rota
  useEffect(() => {
    setDrawerOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  // Bloqueia scroll do body quando drawer aberto
  useEffect(() => {
    if (drawerOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [drawerOpen]);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const SidebarInner = (
    <>
      <div className="h-20 border-b border-border flex items-center justify-between px-5">
        <BrandLogo size="md" />
        {/* Botão de fechar drawer só aparece no mobile — só existe uma vez porque o drawer é o único que passa closable */}
      </div>

      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5 custom-scroll">
        {NAV.filter((item) => user?.role === "admin" || !item.adminOnly).map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            data-testid={item.testId}
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors relative
               ${item.highlight ? "border border-primary/40 bg-primary/5 text-foreground hover:bg-primary/10" : ""}
               ${isActive
                  ? "bg-accent text-foreground font-medium before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-0.5 before:bg-primary before:rounded"
                  : (item.highlight ? "" : "text-muted-foreground hover:text-foreground hover:bg-accent/60")}`
            }
          >
            <item.icon className="w-4 h-4" />
            <span className="truncate">{item.label}</span>
            {item.highlight && <span className="ml-auto text-[9px] uppercase tracking-widest text-primary">Rápido</span>}
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-border p-3 space-y-2">
        <div className="text-[9px] uppercase tracking-[0.18em] text-muted-foreground/70 px-1 text-center" data-testid="fiscal-footer">
          {BRAND.fiscalName}
        </div>
        <div className="flex items-center gap-2 px-1">
          <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center text-xs font-semibold overflow-hidden">
            {user?.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : ((user?.employee_name || user?.name)?.[0] || "A")}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-medium truncate" data-testid="current-user-name">{user?.employee_name || user?.name || "Admin"}</div>
            <div className="text-[10px] text-muted-foreground truncate font-mono">@{user?.username || "admin"}</div>
          </div>
        </div>
        <div className="flex gap-1">
          <Button variant="outline" size="sm" className="flex-1" onClick={toggle} data-testid="theme-toggle-btn">
            {theme === "dark" ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
            <span className="ml-1 text-xs">{theme === "dark" ? "Claro" : "Escuro"}</span>
          </Button>
          <Button variant="outline" size="sm" onClick={handleLogout} data-testid="logout-btn">
            <LogOut className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      {/* Sidebar Desktop (md+) */}
      <aside className="hidden md:flex w-64 shrink-0 border-r border-border bg-card flex-col h-screen sticky top-0" data-testid="sidebar">
        {SidebarInner}
      </aside>

      {/* Drawer Mobile — overlay + panel */}
      {drawerOpen && (
        <div
          className="md:hidden fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
          onClick={() => setDrawerOpen(false)}
          data-testid="drawer-overlay"
        />
      )}
      <aside
        className={`md:hidden fixed top-0 bottom-0 left-0 w-72 max-w-[85vw] bg-card border-r border-border z-50 flex flex-col transition-transform duration-200 ease-out ${
          drawerOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="mobile-drawer"
        aria-hidden={!drawerOpen}
      >
        <button
          className="absolute top-4 right-3 p-2 text-muted-foreground hover:text-foreground rounded-md z-10"
          onClick={() => setDrawerOpen(false)}
          aria-label="Fechar menu"
          data-testid="drawer-close-btn"
        >
          <X className="w-5 h-5" />
        </button>
        {SidebarInner}
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="h-14 md:h-16 border-b border-border bg-background/95 backdrop-blur px-3 md:px-6 flex items-center gap-2 md:gap-4 sticky top-0 z-30">
          {/* Hamburger só mobile */}
          <button
            className="md:hidden p-2 -ml-1 text-muted-foreground hover:text-foreground rounded-md"
            onClick={() => setDrawerOpen(true)}
            aria-label="Abrir menu"
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-5 h-5" />
          </button>

          {/* Logo mobile */}
          <div className="md:hidden flex-1 flex justify-center">
            <BrandLogo size="sm" />
          </div>

          {/* Search desktop */}
          <div className="hidden md:block relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Busca global: cliente, VIN, placa, Ordem de Serviço…"
              className="pl-9 h-9 bg-secondary/40"
              data-testid="global-search-input"
            />
            <span className="kbd hidden lg:inline absolute right-2 top-1/2 -translate-y-1/2">⌘K</span>
          </div>

          {/* Search mobile — botão que abre overlay */}
          <button
            className="md:hidden p-2 text-muted-foreground hover:text-foreground rounded-md"
            onClick={() => setSearchOpen(true)}
            aria-label="Buscar"
            data-testid="mobile-search-btn"
          >
            <Search className="w-5 h-5" />
          </button>

          {/* Actions desktop */}
          <Button
            variant="outline"
            onClick={() => navigate("/check-status")}
            className="hidden md:inline-flex h-9"
            data-testid="header-check-status-btn"
          >
            <ScanLine className="w-4 h-4" />
            <span className="hidden lg:inline">Check Status</span>
          </Button>
          <Button
            onClick={() => navigate("/check-in")}
            className="hidden md:inline-flex h-9"
            data-testid="header-checkin-btn"
          >
            <LogIn className="w-4 h-4" />
            <span className="hidden lg:inline">Check-In</span>
          </Button>
          <div className="hidden xl:block text-xs text-muted-foreground">
            {new Date().toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}
          </div>
        </header>

        {/* Mobile search overlay */}
        {searchOpen && (
          <div
            className="md:hidden fixed inset-0 z-40 bg-background pt-14 px-3 py-3"
            data-testid="mobile-search-overlay"
          >
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  autoFocus
                  placeholder="Cliente, VIN, placa, OS…"
                  className="pl-9 h-11 bg-secondary/40 text-base"
                  data-testid="mobile-global-search-input"
                />
              </div>
              <Button variant="ghost" onClick={() => setSearchOpen(false)} data-testid="mobile-search-close">
                Cancelar
              </Button>
            </div>
          </div>
        )}

        {/* Content — pb extra no mobile para não ficar atrás do bottom nav */}
        <main className="flex-1 p-4 md:p-6 pb-24 md:pb-6 overflow-x-hidden">
          <Outlet />
        </main>

        {/* Bottom Nav — mobile only */}
        <nav
          className="md:hidden fixed bottom-0 left-0 right-0 z-30 border-t border-border bg-card/95 backdrop-blur"
          style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
          data-testid="mobile-bottom-nav"
        >
          <div className="grid grid-cols-5 h-16">
            {BOTTOM_NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testId}
                className={({ isActive }) =>
                  `flex flex-col items-center justify-center gap-1 text-[10px] font-medium relative
                   ${isActive ? "text-primary" : "text-muted-foreground"}`
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-primary rounded-b" />
                    )}
                    <item.icon className="w-5 h-5" />
                    <span className="tracking-wide">{item.label}</span>
                  </>
                )}
              </NavLink>
            ))}
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="flex flex-col items-center justify-center gap-1 text-[10px] font-medium text-muted-foreground"
              data-testid="bnav-more"
            >
              <MoreHorizontal className="w-5 h-5" />
              <span className="tracking-wide">Mais</span>
            </button>
          </div>
        </nav>

        {/* Install PWA banner */}
        <InstallPWA />
      </div>
    </div>
  );
}
